// FILE: src/components/case-studies/exam/CaseExamContext.js
//
// In-exam case-block chrome (Phase 2, PR 6). Renders NOTHING unless the
// current question belongs to a case block, so standard sessions are
// untouched. Three pieces, all self-contained (no App state added):
//
//   1. Entry interstitial — shown once on first arrival at a block's first
//      question ("the next N questions share this scenario"), dismissed with
//      Continue; fires case_exam_block_started.
//   2. Context strip + collapsible case panel — narrative/artifacts scoped
//      to stages ≤ the viewed question's stage (D3: free navigation shows
//      each question's own stage view).
//   3. Case-complete banner — transient, on first arrival at the question
//      after a block; fires case_exam_block_completed.

import React, { useState } from 'react';
import CasePanel from '../learner/CasePanel';
import { trackEvent } from '../../../services/telemetryService';

const T = {
  line: 'var(--line, #e5e8f1)', muted: 'var(--muted, #5a6582)', ink: 'var(--ink, #1a2138)',
  brand: 'var(--brand, #2c63ff)', brandSoft: 'var(--brand-soft, #eaf0ff)',
  good: 'var(--good, #16a571)', goodSoft: 'var(--good-soft, #e6f7ef)', panel: 'var(--panel, #ffffff)',
};

/**
 * Wraps the question area: `children` (revisit banner + QuestionCard) render
 * through untouched on standard sessions, but are SUPPRESSED while a case
 * entry interstitial is on screen — the learner commits to the case before
 * seeing its first question.
 */
export default function CaseExamPanel({ session, currentIndex, currentQuestion, children }) {
  const [open, setOpen] = useState(true);
  const [started, setStarted] = useState(() => new Set());     // interstitials dismissed
  const [completed, setCompleted] = useState(() => new Set()); // completions announced
  // An open exhibit needs room: the scenario strip grows while one is
  // displayed (user feedback via Bryan, 2026-07-13).
  const [artifactOpen, setArtifactOpen] = useState(false);
  const caseBlocks = session && session.caseBlocks;
  if (!caseBlocks) return children || null;

  // Case-complete banner: first question AFTER a block, before it's dismissed.
  const justFinished = Object.values(caseBlocks).find(
    (b) => currentIndex === b.endIndex + 1 && !completed.has(b.id)
  );
  if (!currentQuestion || !currentQuestion.caseId) {
    if (!justFinished) return children || null;
    return (<>
      <div data-testid="case-complete-banner"
        style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 12px', padding: '10px 14px',
          borderRadius: 10, background: T.goodSoft, border: `1px solid ${T.good}` }}>
        <span style={{ fontWeight: 800, color: T.good }}>✓ Case complete</span>
        <span style={{ fontSize: '0.84rem', color: T.muted }}>
          "{justFinished.title}" is finished — back to the regular exam.
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" data-testid="case-complete-dismiss"
          style={{ border: `1px solid ${T.line}`, background: 'transparent', borderRadius: 6, cursor: 'pointer', padding: '2px 10px', fontSize: '0.76rem' }}
          onClick={() => {
            setCompleted((prev) => new Set(prev).add(justFinished.id));
            try { trackEvent('case_exam_block_completed', 'learning', { case_id: justFinished.id, mode: 'full' }); } catch (_) { /* noop */ }
          }}>
          Dismiss
        </button>
      </div>
      {children}
    </>);
  }

  const block = caseBlocks[currentQuestion.caseId];
  if (!block) return children || null;

  // Entry interstitial: first arrival, once per case.
  // Entry interstitial OWNS the screen — the question stays hidden until the
  // learner clicks "Begin case".
  if (currentIndex === block.startIndex && !started.has(block.id)) {
    return (
      <div data-testid="case-interstitial"
        style={{ border: `2px solid ${T.brand}`, background: T.brandSoft, borderRadius: 12,
          padding: '22px 24px', margin: '0 0 14px', textAlign: 'center' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: T.brand }}>
          Case Study
        </div>
        <div style={{ fontSize: '1.15rem', fontWeight: 800, margin: '6px 0' }}>{block.title}</div>
        <div style={{ fontSize: '0.9rem', color: T.muted, marginBottom: 6 }}>
          Questions {block.startIndex + 1}–{block.endIndex + 1} of {session.totalQuestions} belong to this case
          and share the scenario shown alongside each question.
        </div>
        <div style={{ fontSize: '0.8rem', color: T.muted, marginBottom: 14 }}>
          New case information may appear as you progress. The exam clock keeps running.
        </div>
        <button type="button" data-testid="case-interstitial-continue"
          style={{ padding: '10px 26px', borderRadius: 8, border: 'none', background: T.brand, color: '#fff', fontWeight: 700, cursor: 'pointer' }}
          onClick={() => {
            setStarted((prev) => new Set(prev).add(block.id));
            try { trackEvent('case_exam_block_started', 'learning', { case_id: block.id, mode: 'full' }); } catch (_) { /* noop */ }
            setOpen(true);
          }}>
          Begin case →
        </button>
      </div>
    );
  }

  // Context strip + collapsible panel, scoped to the viewed question's stage.
  const stageSeq = currentQuestion.caseStageSequence || 1;
  const visibleStages = (block.stages || []).filter((s) => s.sequence <= stageSeq);
  return (<>
    <div data-testid="case-exam-context" style={{ margin: '0 0 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
        background: T.brandSoft, border: `1px solid ${T.brand}`, borderRadius: open ? '10px 10px 0 0' : 10 }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', color: T.brand }}>🗂️ Case Study</span>
        <span style={{ fontSize: '0.84rem', fontWeight: 700 }}>{block.title}</span>
        <span style={{ fontSize: '0.76rem', color: T.muted }}>
          · question {currentIndex - block.startIndex + 1} of {block.questionCount} in this case
        </span>
        <span style={{ flex: 1 }} />
        <button type="button" data-testid="case-context-toggle"
          style={{ border: `1px solid ${T.line}`, background: T.panel, borderRadius: 6, cursor: 'pointer', padding: '3px 10px', fontSize: '0.76rem', fontWeight: 600 }}
          onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide scenario ▲' : 'Show scenario ▼'}
        </button>
      </div>
      {open && (
        <div data-testid="case-context-body"
          style={{ border: `1px solid ${T.line}`, borderTop: 'none', borderRadius: '0 0 10px 10px',
            background: T.panel, padding: 14, maxHeight: artifactOpen ? '75vh' : '44vh',
            transition: 'max-height 0.25s ease', overflowY: 'auto' }}>
          <CasePanel
            caseMeta={{ title: block.title, learner_role: block.learnerRole }}
            stages={visibleStages}
            latestStage={stageSeq}
            onArtifactViewed={(a) => {
              try { trackEvent('case_artifact_viewed', 'learning', { case_id: block.id, artifact_id: a.id }); } catch (_) { /* noop */ }
            }}
            onArtifactOpenChange={setArtifactOpen}
          />
        </div>
      )}
    </div>
    {children}
  </>);
}
